import { TranslateService } from '@ngx-translate/core';

/**
 * Placeholder mostrato al posto del nome del lead quando l'anagrafica non è più disponibile.
 * Stesso testo già usato in `partner.service.ts`/`admin.service.ts` per i campi sanitari purgati.
 */
export const LEAD_NAME_UNAVAILABLE = 'No longer available';

/** Shown instead of a health-data field the current clinic user is not allowed to see (backend-masked). */
export const HEALTH_DATA_RESTRICTED = 'Restricted';

export class Utils {
    /**
     * Nome da mostrare per un lead. Nome e cognome spariscono in due casi — la retention a 30
     * giorni, che li azzera nei DTO partner, e la cancellazione richiesta dal cliente, che li
     * elimina dal database — e concatenarli produceva "undefined undefined" in tabelle e modali.
     *
     * Il criterio è l'assenza del nome, non i marker `purgedAt`/`cancelledAt`: `SimplePartnerLeadDTO`
     * espone solo `purgedAt`, quindi sui marker resterebbero punti scoperti; e comunque un nome
     * assente non va mai reso come "undefined".
     */
    static formatLeadName(lead?: { firstname?: string | null; lastname?: string | null } | null): string {
        const name = [lead?.firstname, lead?.lastname]
            .map((part) => part?.trim())
            .filter((part) => !!part)
            .join(' ');

        return name || LEAD_NAME_UNAVAILABLE;
    }

    /**
     * Valore da mostrare per un campo sanitario della richiesta di preventivo — motivazione, arcata,
     * trattamento, dettagli. `LeadRetentionService.purgeQuoteRequestHealthData` li azzera sia alla
     * scadenza dei 30 giorni sia sulla cancellazione richiesta dal cliente, e senza placeholder la
     * clinica vedeva una cella vuota o un trattino, indistinguibile da un campo mai compilato.
     *
     * A differenza del nome, qui il criterio NON può essere la sola assenza: `request_motivation` e
     * i suoi pari sono colonne nullable, senza `@NotNull` sul DTO, e l'obbligatorietà vive solo nel
     * form pubblico. Una richiesta arrivata da un percorso che non popola le offer details verrebbe
     * quindi mostrata come cancellata pur non essendolo, quindi il placeholder è vincolato al
     * marker `lead.purgedAt`.
     *
     * @param value      valore già formattato per la visualizzazione (etichetta tradotta, capitalizzata…)
     * @param lead       lead della richiesta, da cui si legge `purgedAt`
     * @param fallback   cosa mostrare quando il campo è vuoto ma il lead NON è purgato
     * @param restricted true quando il backend ha mascherato il campo per mancanza del permesso
     *                   "View patient health data" (`healthDataRestricted` sul DTO): vince su tutto,
     *                   incluso il placeholder del purge, perché qui l'assenza del dato non dipende
     *                   dalla retention ma dai permessi dell'utente corrente.
     */
    static formatHealthDataField(
        value: string | null | undefined,
        lead?: { purgedAt?: string | Date | null } | null,
        fallback: string = '-',
        restricted: boolean = false,
    ): string {
        if (restricted) return HEALTH_DATA_RESTRICTED;

        const text = value?.trim();
        if (text) return text;

        return lead?.purgedAt ? LEAD_NAME_UNAVAILABLE : fallback;
    }

    /**
     * Risolve il nome del paese a partire dal codice ISO-3166 alpha-2 usando le chiavi
     * `country.<CODICE>` dei file i18n. Nei pannelli di controllo (admin/partner/support) la
     * lingua attiva è forzata a 'en' (vedi LangResolverService.ENGLISH_ONLY_PREFIXES), quindi il
     * nome esce sempre in inglese — a differenza di `lead.country`, che è il nome memorizzato a DB
     * nella lingua con cui il paese è stato inserito la prima volta (Country.name è una sola
     * colonna condivisa, quindi può essere "Brasile" invece di "Brazil").
     *
     * Se il codice manca o non è mappato, ripiega sul nome memorizzato (`fallback`).
     */
    static resolveCountryName(
        translate: TranslateService,
        countryCode?: string | null,
        fallback?: string | null,
    ): string {
        if (!countryCode) {
            return fallback ?? '';
        }
        let code = countryCode.toUpperCase();
        if (code === 'EN') {
            code = 'GB'; // 'en' è usato come codice bandiera per il Regno Unito
        }
        const key = `country.${code}`;
        const translated = translate.instant(key);
        return translated && translated !== key ? translated : (fallback ?? countryCode);
    }

    static getFlagSrc(countryCode: string): string {
        countryCode = countryCode.toLowerCase();
        if (countryCode === 'en') countryCode = 'gb';
        if (countryCode !== 'wl') {
            return `https://flagcdn.com/w320/${countryCode}.png`;
        } else {
            return '/assets/public.png';
        }
    }

    static getFlagName(countryCode: string): string {
        switch (countryCode.toLowerCase()) {
            case 'al': return 'Albanian';
            case 'fr': return 'French';
            case 'it': return 'Italian'
            case 'gb':
            case 'en': return 'English';
            case 'es': return 'Spanish';
            case 'de': return 'German';
            case 'ru': return 'Russian';
            case 'pt': return 'Portuguese';
            case 'wl': return 'All languages';
            default: return countryCode.toUpperCase();
        }
    }

}